import React, { useContext, useEffect, useState, useMemo } from "react";
import { ProsContext } from "../context/ProsContext";
import { AuthContext } from "../context/AuthContext";
import { getItem } from "../api/jobExplorer";
import { useParams, useNavigate } from "react-router-dom";
import { HistoryContext } from "../context/HistoryContext";
import {
  ProfileMatchBlock,
  PremiumModal,
  CompleteProfileModal,
  ProfileAnalysisView
} from "../components/jobProDesk";

export default function JobProDesk({ initActivity, handleCreateItem }) {
  const { user, updateUser } = useContext(AuthContext);
  const { jobId, gender } = useParams();
  const [jobFromApi, setJobFromApi] = useState(null);
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const { itemsPro, updateSynthesis } = useContext(ProsContext);
  const imgPrefix = gender === 'boy' ? 'm' : 'f';
  const { getHistoryObj } = useContext(HistoryContext);

  // États pour les modales et vues
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('');
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isLoadingSynthesis, setIsLoadingSynthesis] = useState(false);
  const [synthesisError, setSynthesisError] = useState(false);

  const history = getHistoryObj(jobId) || [];

  // États dérivés de l'historique
  const { hasVisitedFiche, hasStartedQuiz, hasCompletedQuiz, hasStartedGnf, hasCompletedGnf, hasDiscussed } = useMemo(() => {
    const types = history.map(h => h.type);
    return {
      hasVisitedFiche: types.includes('JOB_PAGE'),
      hasStartedQuiz: types.includes('QUIZZ_STARTED'),
      hasCompletedQuiz: types.includes('QUIZZ_ENDED'),
      hasStartedGnf: types.includes('GRIT_AND_FIT_SCENARIO_STARTED'),
      hasCompletedGnf: types.includes('GRIT_AND_FIT_SCENARIO_ENDED'),
      hasDiscussed: types.includes('"EXPERT_CHAT_STARTED"')
    };
  }, [history]);

  useEffect(() => {
    if (jobId) {
      (async () => {
        setJobFromApi(await getItem(jobId));
      })();
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId && itemsPro[jobId]) {
      setPro(itemsPro[jobId]);
    }
  }, [jobId, itemsPro]);

  const goToChat = async () => {
    const qualities = jobFromApi._qualities.map((quality) => `${quality._quality.label} : ${quality.explanation}`).join("\n");
    const level_of_education = jobFromApi._study_levels.map((study_level) => study_level._study_level.label).join(", ");
    const salary = `Salaire début de carrière de ${jobFromApi.salary_min}€ pouvant aller jusqu'à ${jobFromApi.salary_max}€. ${jobFromApi.salary_explanation}`;
    const salary_evolution_explanation = jobFromApi.salary_evolutions.map((salary_evolution) =>
      `${salary_evolution.experience} (${salary_evolution.experience_years}) gagne environ ${salary_evolution.experience_salary}. Role : ${salary_evolution.experience_role}.`
    ).join("\n");

    const values = {
      id: jobFromApi.id,
      title: gender === 'boy' ? jobFromApi.title_m : jobFromApi.title_f,
      title_for_prompt: gender === 'boy' ? jobFromApi.title_for_prompt_m : jobFromApi.title_for_prompt_f,
      name: pro.name,
      picture: gender === 'boy' ? jobFromApi.aws_pictures_m[1] : jobFromApi.aws_pictures_f[1],
      qualities: qualities,
      salary: salary,
      salary_evolution_explanation: salary_evolution_explanation,
      level_of_education: level_of_education,
      gender,
      from: "job_desk"
    };
    await handleCreateItem(values);
    navigate(`chat`);
  };

  const goToQuiz = async () => {
    const values = {
      jobId: jobId,
      title: pro.title,
      photo_url: `https://jobs-pictures.s3.eu-north-1.amazonaws.com/photos_${imgPrefix}/${jobId}_1.jpg`,
      type: 'quizz',
      made_for_you: user.age < 16 ? jobFromApi.made_for_you_14_v2 : jobFromApi.made_for_you_16_v2,
      from: "job_desk"
    };
    await initActivity(values);
    navigate(`quizz`);
  };

  const goToGritAndFit = async () => {
    const values = {
      jobId: jobId,
      title: pro.title,
      photo_url: `https://jobs-pictures.s3.eu-north-1.amazonaws.com/photos_${imgPrefix}/${jobId}_1.jpg`,
      type: 'grit-and-fit',
      made_for_you: user.age < 16 ? jobFromApi.made_for_you_14_v2 : jobFromApi.made_for_you_16_v2,
      from: "job_desk"
    };
    await initActivity(values);
    navigate(`grit-and-fit`);
  };

  const goToFiche = () => {
    navigate(`/metiers/${jobId}/${gender}`);
  };

  const handleAskQuestion = (question) => {
    setShowAnalysis(false);
    navigate(`chat?q=${encodeURIComponent(question)}`);
  };

  // Générer la synthèse
  const generateSynthesis = async () => {
    setSynthesisError(false);
    setIsLoadingSynthesis(true);
    try {
      await updateSynthesis(jobId);
    } catch (error) {
      console.error('Erreur lors de la génération de la synthèse:', error);
      setSynthesisError(true);
    } finally {
      setIsLoadingSynthesis(false);
    }
  };

  // Ouvrir l'analyse et générer la synthèse si nécessaire
  const handleOpenAnalysis = async () => {
    setShowAnalysis(true);

    // Si pas de synthèse, la générer
    const currentSynthesis = itemsPro[jobId]?.survey_synthesis;
    if (!currentSynthesis || Object.keys(currentSynthesis).length === 0) {
      await generateSynthesis();
    }
  };

  // Vue Analyse
  if (showAnalysis) {
    return (
      <div className="relative overflow-y-auto flex-1 flex bg-gray-900">
        <ProfileAnalysisView
          pro={pro ? { name: pro.name, photoUrl: `https://jobs-pictures.s3.eu-north-1.amazonaws.com/photos_${imgPrefix}/${jobId}_1.jpg` } : null}
          jobTitle={pro?.title}
          onBack={() => setShowAnalysis(false)}
          onAskQuestion={handleAskQuestion}
          synthesis={itemsPro[jobId]?.survey_synthesis}
          isLoading={isLoadingSynthesis}
          hasError={synthesisError}
          onRetry={generateSynthesis}
        />
      </div>
    );
  }

  // Vue Dashboard
  return (
    <div className="relative flex-1 flex bg-gray-900">
      <div className="flex flex-1 flex-col h-full gap-4 px-4 md:py-8 pt-14 w-full mx-auto max-w-lg ">
        {/* Header : Avatar + Nom + Job */}
        {pro && (
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-700">
              <img
                src={`https://jobs-pictures.s3.eu-north-1.amazonaws.com/photos_${imgPrefix}/${jobId}_1.jpg`}
                alt={pro.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h1 className="text-white text-xl font-semibold">{pro.name}</h1>
              <p className="text-gray-400 text-sm">{pro.title}</p>
            </div>
          </div>
        )}

        {/* BLOC : Toi & ce métier */}
        <ProfileMatchBlock
          isPremium={user?.is_premium}
          onOpenPremiumModal={() => {
            const jobTitle = gender === 'boy' ? jobFromApi?.title_m : jobFromApi?.title_f
            const startsWithVowel = /^[aeiouàâäéèêëïîôùûü]/i.test(jobTitle || '')
            setPremiumModalReason(`découvrir si le métier ${startsWithVowel ? "d'" : "de "}${jobTitle} lui correspond`)
            setShowPremiumModal(true);
          }}
          onOpenCompleteProfileModal={() => setShowCompleteProfileModal(true)}
          onOpenAnalysis={handleOpenAnalysis}
        />

        {/* BLOC : Discuter avec [nom] */}
        {pro && (
          <div
            onClick={goToChat}
            className="bg-slate-800 rounded-xl p-5 transition-colors cursor-pointer border border-slate-700"
          >
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-slate-600">
                  <img
                    src={`https://jobs-pictures.s3.eu-north-1.amazonaws.com/photos_${imgPrefix}/${jobId}_1.jpg`}
                    alt={pro.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Indicateur en ligne */}
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-slate-800"></div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-medium">Discuter avec {pro.name}</h3>
                  <span className="flex items-center gap-1 text-xs text-emerald-400">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
                    {hasDiscussed ? "En ligne" : "Disponible"}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  {hasDiscussed ? "On en était où ?" : "Ravie de te rencontrer"}
                </p>
              </div>
              <span className="text-2xl">💬</span>
            </div>
          </div>
        )}

        {/* BLOC : Trois cartes secondaires */}
        <div className="grid grid-cols-3 gap-3">
          {/* Le métier */}
          <div
            onClick={goToFiche}
            className={`relative rounded-xl p-4 transition-colors cursor-pointer border text-center ${
              hasVisitedFiche
                ? 'bg-teal-900/20 border-teal-800/50'
                : 'bg-slate-800 border-slate-700'
            }`}
          >
            <span className="text-2xl mb-2 block">📄</span>
            <h3 className="text-white text-sm font-medium mb-1">Le métier</h3>
            <p className="text-gray-500 text-xs">Salaire, Études, Quotidien</p>
          </div>

          {/* Teste-toi */}
          <div
            onClick={goToQuiz}
            className={`relative rounded-xl p-4 transition-colors cursor-pointer border text-center ${
              hasStartedQuiz
                ? 'bg-teal-900/20 border-teal-800/50'
                : 'bg-slate-800 border-slate-700'
            }`}
          >
            {hasCompletedQuiz && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            )}
            <span className="text-2xl mb-2 block">🎯</span>
            <h3 className="text-white text-sm font-medium mb-1">Teste-toi</h3>
            <p className="text-gray-500 text-xs">5 min, 10 questions</p>
            
          </div>

          {/* Vis une journée */}
          <div
            onClick={goToGritAndFit}
            className={`relative rounded-xl p-4 transition-colors cursor-pointer border text-center ${
              hasStartedGnf
                ? 'bg-teal-900/20 border-teal-800/50'
                : 'bg-slate-800 border-slate-700'
            }`}
          >
            {hasCompletedGnf && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            )}
            <span className="text-2xl mb-2 block">🎮</span>
            <h3 className="text-white text-sm font-medium mb-1">Vis une journée</h3>
            <p className="text-gray-500 text-xs">10 min, 3 situations</p>
            
          </div>
        </div>
      </div>

      {/* Modales */}
      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context="Pour débloquer les 3 autres questionnaires et l'analyse personnalisé."
        reason={premiumModalReason}
      />
      <CompleteProfileModal
        isOpen={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
      />
    </div>
  );
}